import { Theme } from '@mui/material';
import { getSize } from '../../utils/get-size';
import { styled } from '../../utils/styled';
import { Typography } from '../Typography';

export type BadgeSize = 'small' | 'large';
export type BadgeColor = 'warning' | 'error';

type InternalBadgeSize = BadgeSize | 'tiny';

const sizes: Record<InternalBadgeSize, number> = {
  tiny: 8,
  small: 16,
  large: 24,
};

interface BadgeProps {
  readonly size: BadgeSize;
  readonly count?: number;
  readonly color: BadgeColor;
  readonly 'data-testid'?: string;
}

const colorMapper = (theme: Theme) => ({
  error: theme.palette.red[600],
  warning: theme.palette.yellow[600],
});

const StyledBadge = styled('div')<{
  sizing: InternalBadgeSize;
  color: BadgeColor;
}>`
  ${({ sizing }) => getSize(sizes[sizing])};
  background-color: ${({ theme, color }) => colorMapper(theme)[color]};
  color: ${({ theme }) => theme.palette.paper[200]};
  border-radius: ${({ theme }) => theme.borderRadii.circular};
  justify-content: center;
  display: flex;
  align-items: center;
  animation: bounce 1s ease 2;
  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-20%);
    }
    60% {
      transform: translateY(-10%);
    }
  }
`;

const getSizeLabel = (size: BadgeSize, count?: number): InternalBadgeSize => {
  if (!count && size === 'small') {
    return 'tiny';
  }
  if (!count && size === 'large') {
    return 'small';
  }
  return size;
};

const getCount = (count?: number) => {
  if (count === 0) {
    return '';
  }
  if (count && count > 9) {
    return '9+';
  }
  return count;
};

export function Badge({
  count,
  size,
  color,
  'data-testid': dataTestId,
}: BadgeProps) {
  return (
    <StyledBadge
      color={color}
      data-testid={dataTestId}
      sizing={getSizeLabel(size, count)}
    >
      <Typography variant={size === 'small' ? 'captionStrong' : 'body'}>
        {getCount(count)}
      </Typography>
    </StyledBadge>
  );
}
